// Firm self-healing (F02/F05): findings from `firmheal.status` (partners only). A finding's detail — what is wrong, the evidence,
// the repair plan with its risk and rollback, and the plain-language explanation — is the engine's `firmheal.status --finding`,
// asked through the inbox. Approve and reject are the signed-in partner's acts and stay off until the explanation is on screen.
import { useState } from "react";
import { Card, PageHeader, StatusPill } from "../../components";
import { fmtStamp } from "../../lib/format";
import { ActButton, ApprovalPanel, Computed, ModelGate, ReadView, Tiles, useActs, useEngineRead, useFirmModels } from "./common";

type Doc = Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
const num = (f: Doc) => Number(String(f.finding ?? f.id).split("-").pop());

export function HealingPage() {
  const ms = useFirmModels(["firm_healing"]);
  const [act, drawer] = useActs();
  const [open, setOpen] = useState<number | null>(null);
  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <PageHeader title="Firm self-healing" subtitle="What the firm-level detectors found, the engine's explanation and repair plan, and the partner's decision."
        actions={<ActButton command="firmheal.scan" label="Scan now" onClick={() => act({ command: "firmheal.scan", title: "Scan the firm for problems" })} />} />
      {ms.error && <p role="alert" className="text-sm text-blocked">{ms.error}</p>}
      {ms.data && (
        <ModelGate<Doc> kind="firm_healing" m={ms.data.firm_healing} label="Firm self-healing">
          {(d, m) => {
            const fs: Doc[] = d.findings ?? [];
            const counts: Record<string, number> = d.counts ?? {};
            return (
              <>
                <Tiles items={[
                  { label: "Findings", value: fs.length, tone: fs.length ? "waiting" : "done" },
                  ...Object.entries(counts).map(([k, v]) => ({ label: k.replace(/_/g, " "), value: v })),
                  { label: "Recurring", value: (d.recurring ?? []).length, tone: (d.recurring ?? []).length ? "blocked" as const : undefined },
                  { label: "Last scan", value: d.recent_scans?.[0] ? fmtStamp(d.recent_scans[0].at) : "never" },
                ]} />
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
                  <Card title="Findings" action={<Computed m={m} />} bodyClass="p-0">
                    {fs.length === 0 ? <p className="p-4 text-sm text-muted">No finding. The detectors found nothing wrong on the last scan.</p> : (
                      <ul className="divide-y divide-line">
                        {fs.map((f) => (
                          <li key={f.finding}>
                            <button type="button" onClick={() => setOpen(num(f))} aria-pressed={open === num(f)} className={`w-full px-3 py-2 text-left text-sm ${open === num(f) ? "bg-brand/10" : "hover:bg-sunken"}`}>
                              <span className="flex flex-wrap items-center gap-2"><StatusPill state={f.status} /><StatusPill state={f.severity === "fail" ? "failed" : "warned"} label={f.severity} />
                                <span className="font-mono text-xs">{f.finding}</span><span className="text-xs text-muted">{f.detector}</span></span>
                              <span className="mt-0.5 block break-words">{f.item}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Card>
                  <div>{open !== null ? <FindingDetail id={open} act={act} /> : <p className="text-sm text-muted">Choose a finding to see the engine's explanation and decide.</p>}</div>
                </div>
              </>
            );
          }}
        </ModelGate>
      )}
      {drawer}
    </div>
  );
}

function FindingDetail({ id, act }: { id: number; act: ReturnType<typeof useActs>[0] }) {
  const r = useEngineRead<Doc>("firmheal.status", { finding: id }, `finding HF-${id}`);
  return (
    <Card title={`Finding HF-${id}`}>
      <ReadView r={r} label={`finding HF-${id}`}>
        {(f) => (
          <ApprovalPanel explanation={f.explanation} missing="No explanation yet: ask the engine to explain it before anyone decides."
            evidence={
              <div className="space-y-2 text-sm">
                <p><span className="text-muted">What is wrong:</span> {f.evidence?.detail}</p>
                <p className="text-xs text-muted">Checked: {f.evidence?.checked} · how: {f.evidence?.how}</p>
                {f.plan && (
                  <div className="rounded-md border border-line p-2">
                    <p><span className="text-muted">Plan ({f.plan.kind}):</span> {f.plan.does}</p>
                    <p className="text-xs text-muted">Risk: {f.plan.risk} · rollback: {f.plan.rollback} · verify: {f.plan.verify}</p>
                    {(f.plan.steps ?? []).length > 0 && <ol className="mt-1 list-inside list-decimal text-xs">{(f.plan.steps as Doc[]).map((s, i) => <li key={i}><span className="font-mono">{s.command}</span> — {s.why}</li>)}</ol>}
                  </div>
                )}
                {f.decided_by && <p className="text-xs">Decided by {f.decided_by} on {fmtStamp(f.decided_at)}{f.decision_note ? `: ${f.decision_note}` : ""}</p>}
              </div>
            }
            decisions={
              <>
                {!f.explanation && <ActButton command="firmheal.explain" label="Ask for the explanation" onClick={() => act({ command: "firmheal.explain", fixed: { finding: id }, title: `Explain finding HF-${id}` })} />}
                <DecideButtons f={f} id={id} act={act} />
              </>
            } />
        )}
      </ReadView>
    </Card>
  );
}

function DecideButtons({ f, id, act }: { f: Doc; id: number; act: ReturnType<typeof useActs>[0] }) {
  const ready = !!f.explanation && (f.status === "explained" || f.status === "open");
  return (
    <>
      {ready && f.status === "explained" && <ActButton command="firmheal.approve" label="Approve the plan…" tone="primary" onClick={() => act({ command: "firmheal.approve", fixed: { finding: id }, title: `Approve the plan for HF-${id}` })} />}
      {ready && <ActButton command="firmheal.reject" label="Reject…" tone="danger" onClick={() => act({ command: "firmheal.reject", fixed: { finding: id }, title: `Reject the plan for HF-${id}` })} />}
      {f.status === "approved" && <ActButton command="firmheal.apply" label="Apply the approved plan" tone="primary" onClick={() => act({ command: "firmheal.apply", fixed: { finding: id }, title: `Apply the plan for HF-${id}` })} />}
      {(f.status === "applied" || f.status === "escalated") && <ActButton command="firmheal.verify" label="Verify" onClick={() => act({ command: "firmheal.verify", fixed: { finding: id }, title: `Verify HF-${id}` })} />}
      {!ready && f.status !== "approved" && f.status !== "applied" && f.status !== "escalated" && <span className="text-sm text-muted">Nothing to decide in state “{f.status}”.</span>}
    </>
  );
}
